<template>
  <div class="multi-net-turn-page">
    <!-- 主内容区 -->
    <div class="page-body" v-loading="pageLoading">
      <!-- 空状态 -->
      <div v-if="cardList.length === 0" class="empty-state">
        <i class="el-icon-s-order empty-icon"></i>
        <p class="empty-title">暂无待转套餐的组网卡</p>
        <p class="empty-desc">您可以通过下方按钮添加需要转套餐的卡号</p>
        <el-button type="primary" icon="el-icon-circle-plus-outline" @click="openAddDialog">添加卡号</el-button>
      </div>

      <!-- 卡片列表 -->
      <el-collapse v-else v-model="activeNames" class="card-collapse">
        <el-collapse-item v-for="(item, index) in cardList" :key="item.iccid + index" :name="index">
          <!-- 标题栏 -->
          <div slot="title" class="collapse-title-row">
            <div class="title-left">
              <span class="iccid-label">ICCID：</span>
              <span class="iccid-value">{{ item.iccid }}</span>
              <el-tag v-if="item.targetIccid" type="success" size="small" class="target-tag">
                目标 {{ item.targetIccid }}
              </el-tag>
            </div>
            <div class="title-right">
              <span v-if="item.error" class="error-tip">
                <i class="el-icon-error"></i>
                {{ item.errorMsg || '请完善信息' }}
              </span>
              <el-button
                type="danger"
                size="small"
                plain
                icon="el-icon-delete"
                @click.stop="removeCard(item, index)"
              >移除卡号</el-button>
            </div>
          </div>

          <!-- 套餐表格 -->
          <div class="section-block">
            <div class="block-label">可选套餐</div>
            <el-table
              :data="item.orderItems"
              :ref="el => setTableRef(el, index)"
              border
              stripe
              size="small"
              max-height="300"
              @selection-change="(selection) => handleSelectionChange(selection, item)"
              row-key="orderNum"
            >
              <el-table-column type="selection" align="center" width="55" reserve-selection></el-table-column>
              <el-table-column prop="orderNum" label="订单号" align="center" min-width="160" show-overflow-tooltip></el-table-column>
              <el-table-column prop="productName" label="套餐名称" align="center" min-width="160" show-overflow-tooltip></el-table-column>
              <el-table-column prop="productStatus" label="套餐状态" align="center" min-width="100">
                <template slot-scope="scope">
                  <el-tag v-if="scope.row.productStatus === '使用中'" type="success" size="small">{{ scope.row.productStatus }}</el-tag>
                  <span v-else>{{ scope.row.productStatus }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="expireTime" label="套餐过期时间" align="center" min-width="160"></el-table-column>
              <el-table-column prop="type" label="套餐类型" align="center" min-width="100"></el-table-column>
            </el-table>
            <div class="table-summary">
              <span>已选 <strong>{{ (item.selectOrderItems || []).length }}</strong> 个套餐</span>
              <el-button type="text" size="small" @click="selectAllPackages(item, index)">全选</el-button>
              <el-button type="text" size="small" @click="clearAllPackages(item, index)">取消全选</el-button>
            </div>
          </div>

          <!-- 表单区 -->
          <el-form
            :model="item"
            :ref="'transferForm' + index"
            label-width="140px"
            class="transfer-form"
            :rules="formRules"
          >
            <el-row :gutter="20">
              <el-col :span="24">
                <el-form-item label="代理商层级：">
                  <span class="agent-text">{{ item.companyName || '-' }}</span>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
                <el-form-item label="组网号/ICCID" prop="targetIccid">
                  <el-input
                    v-model="item.targetIccid"
                    placeholder="请输入目标组网号/ICCID"
                    clearable
                    maxlength="30"
                    @blur="validateItem(item)"
                  ></el-input>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
                <el-form-item label="原组网号备注">
                  <el-input
                    v-model="item.remark"
                    type="textarea"
                    :rows="3"
                    maxlength="300"
                    show-word-limit
                    placeholder="请输入原组网号备注"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
                <el-form-item label="目标组网号备注">
                  <el-input
                    v-model="item.targetRemark"
                    type="textarea"
                    :rows="3"
                    maxlength="300"
                    show-word-limit
                    placeholder="请输入目标组网号备注"
                  ></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            
          </el-form>
        </el-collapse-item>
      </el-collapse>

      <!-- 添加卡号按钮 -->
      <div v-if="cardList.length > 0" class="add-card-bar">
        <el-button type="primary" plain icon="el-icon-circle-plus-outline" style="width: 100%" @click="openAddDialog">
          添加卡号
        </el-button>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div class="page-footer" v-if="cardList.length > 0">
      <div class="footer-summary">
        <span>待转卡数：<strong>{{ cardList.length }}</strong></span>
        <span>已选套餐：<strong>{{ totalSelectedPackages }}</strong></span>
      </div>
      <el-button
        type="primary"
        size="medium"
        :loading="submitLoading"
        :disabled="!canSubmit"
        @click="submitTransfer"
      >开始批量转套餐</el-button>
    </div>

    <!-- 添加卡号弹窗 -->
    <el-dialog
      title="添加转套餐卡号"
      :visible.sync="addDialogVisible"
      width="520px"
      append-to-body
      :close-on-click-modal="false"
      @close="resetAddDialog"
    >
      <el-form :model="addForm" ref="addFormRef" :rules="addFormRules" label-width="0">
        <el-form-item prop="iccids">
          <el-input
            v-model="addForm.iccids"
            type="textarea"
            :rows="8"
            placeholder="请输入ICCID / 组网号，支持多个，每行一个或逗号分隔"
          ></el-input>
        </el-form-item>
        <div class="add-stat">已识别 <strong>{{ addCardCount }}</strong> 个卡号</div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="addDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="addLoading" @click="confirmAddCards">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { transferProductList , batchTransferProduct} from "@/api/api.js";

export default {
  name: 'multiNetTurnProduct',
  data() {
    return {
      pageLoading: false,
      submitLoading: false,
      addLoading: false,
      activeNames: [0],
      cardList: [],
      tableRefs: [],
      addDialogVisible: false,
      addForm: {
        iccids: ''
      },
      formRules: {
        targetIccid: [
          { required: true, message: '请输入目标ICCID', trigger: 'blur' }
        ]
      },
      addFormRules: {
        iccids: [
          { required: true, message: '请输入卡号', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    // 路由传入的初始卡号，支持 query 或 params
    routeIccids() {
      const params = this.$route.params && this.$route.params.iccids
      return params || ''
    },
    addCardCount() {
      return this.parseIccidInput(this.addForm.iccids).length
    },
    totalSelectedPackages() {
      return this.cardList.reduce((sum, item) => {
        return sum + ((item.selectOrderItems && item.selectOrderItems.length) || 0)
      }, 0)
    },
    canSubmit() {
      if (this.cardList.length === 0) return false
      return this.cardList.every(item => {
        return !!item.targetIccid && item.selectOrderItems && item.selectOrderItems.length > 0
      })
    }
  },
  created() {
    this.initCards()
  },
  methods: {
    // 解析输入：按行或逗号分隔，去空去重
    parseIccidInput(str) {
      if (!str) return []
      return str
        .split(/[\n,，]/)
        .map(s => s.trim())
        .filter(s => !!s)
        .filter((s, idx, arr) => arr.indexOf(s) === idx)
    },

    // 设置表格 ref（v-for 中动态绑定）
    setTableRef(el, index) {
      if (el) {
        this.$set(this.tableRefs, index, el)
      } else {
        this.$delete(this.tableRefs, index)
      }
    },

    // 初始化：优先读取路由传入的卡号
    initCards() {
      const iccids = this.parseIccidInput(this.routeIccids)
      if (iccids.length === 0) return
      this.loadCards(iccids)
    },

    // 批量加载卡数据
    loadCards(iccids) {
      this.pageLoading = true
      transferProductList(iccids.join(','))
        .then(res => {
          if (res.data.code === 200) {
            const data = res.data.data || []
            this.cardList = data.map(item => this.normalizeCard(item))
            this.activeNames = this.cardList.map((_, idx) => idx)
            // 等待 DOM 渲染后再同步选中状态
            this.$nextTick(() => {
              this.cardList.forEach((item, idx) => {
                this.syncTableSelection(item, idx)
              })
            })
          } else {
            this.$message.error(res.data.message || '获取转套餐数据失败')
          }
        })
        .catch(err => {
          console.error(err)
          this.$message.error('获取转套餐数据失败')
        })
        .finally(() => {
          this.pageLoading = false
        })
    },

    // 统一卡片数据结构，补充缺省字段
    normalizeCard(item) {
      return {
        iccid: item.iccid || '',
        targetIccid: item.targetIccid || '',
        companyName: item.companyName || '',
        orderItems: Array.isArray(item.orderItems) ? item.orderItems : [],
        remark: item.remark || '',
        targetRemark: item.targetRemark || '',
        saleRemark: item.saleRemark || '',
        targetSaleRemark: item.targetSaleRemark || '',
        selectOrderItems: [],
        error: false,
        errorMsg: ''
      }
    },

    // 同步单张卡的表格选中状态（数据 → UI）
    syncTableSelection(item, index) {
      const table = this.tableRefs[index]
      if (!table) return
      const selected = new Set(item.selectOrderItems || [])
      item.orderItems.forEach(row => {
        const isSelected = selected.has(row.orderNum)
        table.toggleRowSelection(row, isSelected)
      })
    },

    // 套餐选择变化
    handleSelectionChange(selection, item) {
      item.selectOrderItems = selection.map(row => row.orderNum)
      this.validateItem(item)
    },

    // 全选当前卡套餐
    selectAllPackages(item, index) {
      item.selectOrderItems = item.orderItems.map(row => row.orderNum)
      this.syncTableSelection(item, index)
      this.validateItem(item)
    },

    // 取消全选当前卡套餐
    clearAllPackages(item, index) {
      item.selectOrderItems = []
      this.syncTableSelection(item, index)
      this.validateItem(item)
    },

    // 校验单条卡片
    validateItem(item) {
      let errorMsg = ''
      if (!item.targetIccid) {
        errorMsg = '目标ICCID未填写'
      } else if (item.targetIccid === item.iccid) {
        errorMsg = '目标ICCID不能与源ICCID相同'
      } else if (!item.selectOrderItems || item.selectOrderItems.length === 0) {
        errorMsg = '未选择套餐'
      }
      item.error = !!errorMsg
      item.errorMsg = errorMsg
      return !item.error
    },

    // 移除卡片
    removeCard(item, index) {
      this.$confirm(`确定移除卡号为 ${item.iccid} 的转套餐记录？`, '移除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          this.cardList.splice(index, 1)
          if (this.cardList.length === 0) {
            this.activeNames = []
          } else {
            // 保持面板展开状态同步
            this.activeNames = this.cardList.map((_, idx) => idx)
          }
          this.$message.success('移除成功')
        })
        .catch(() => {})
    },

    // 打开添加弹窗
    openAddDialog() {
      this.addDialogVisible = true
      this.$nextTick(() => {
        this.$refs.addFormRef && this.$refs.addFormRef.resetFields()
      })
    },

    // 重置添加弹窗
    resetAddDialog() {
      this.addForm.iccids = ''
      this.$refs.addFormRef && this.$refs.addFormRef.resetFields()
    },

    // 确认添加卡号
    confirmAddCards() {
      this.$refs.addFormRef.validate(valid => {
        if (!valid) return
        const iccids = this.parseIccidInput(this.addForm.iccids)
        if (iccids.length === 0) {
          this.$message.warning('请输入有效的卡号')
          return
        }
        // 过滤已存在的卡号
        const existed = this.cardList.map(c => c.iccid)
        const newIccids = iccids.filter(iccid => !existed.includes(iccid))
        if (newIccids.length === 0) {
          this.$message.warning('这些卡号已存在于列表中')
          return
        }
        this.addLoading = true
        transferProductList(newIccids.join(','))
          .then(res => {
            if (res.data.code === 200) {
              const data = res.data.data || []
              const newCards = data.map(item => this.normalizeCard(item))
              if(newCards.length === 0){
                this.$message.error('没有查询到可转移套餐')
                return
              }
              this.cardList.push(...newCards)
              this.activeNames = this.cardList.map((_, idx) => idx)
              this.$nextTick(() => {
                newCards.forEach((_, i) => {
                  const idx = this.cardList.length - newCards.length + i
                  this.syncTableSelection(this.cardList[idx], idx)
                })
              })
              this.addDialogVisible = false
              this.$message.success(`成功添加 ${newCards.length} 张卡`)
            } else {
              this.$message.error(res.data.message || '添加失败')
            }
          })
          .catch(err => {
            console.error(err)
          })
          .finally(() => {
            this.addLoading = false
          })
      })
    },

    // 提交批量转套餐
    submitTransfer() {
      // 先校验所有卡片
      let valid = true
      this.cardList.forEach(item => {
        if (!this.validateItem(item)) {
          valid = false
        }
      })
      if (!valid) {
        this.$message.warning('请完善标红卡片的必填信息')
        return
      }
      this.$confirm('确认提交批量转套餐申请？', '操作确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          this.submitLoading = true
          const params = this.cardList.map(item => ({
            iccid: item.iccid,
            targetIccid: item.targetIccid,
            selectOrderItems: item.selectOrderItems,
            remark: item.remark,
            targetRemark: item.targetRemark,
          }))
          batchTransferProduct(params)
            .then(res => {
              if (res.data.code === 200) {
                this.$message.success('批量转套餐申请已提交，任务中心查看结果')
                this.$store.dispatch('tagsView/delView', this.$route).then(() => {
                  this.$router.push({ path: '/device/card/multiNet' })
                })
              } else {
                this.$message.error(res.data.message || '提交失败')
              }
            })
            .catch(err => {
              console.error(err)
            })
            .finally(() => {
              this.submitLoading = false
            })
        })
        .catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.multi-net-turn-page {
  padding: 16px;
  background: #f0f2f5;
  min-height: 100%;
  padding-bottom: 80px;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;
    border-radius: 4px;
    padding: 12px 20px;
    margin-bottom: 16px;

    .page-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }
  }

  .page-body {
    background: #fff;
    border-radius: 4px;
    padding: 20px;
    min-height: calc(100vh - 220px);

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 80px 20px;
      color: #909399;

      .empty-icon {
        font-size: 72px;
        margin-bottom: 16px;
        color: #c0c4cc;
      }

      .empty-title {
        font-size: 16px;
        color: #606266;
        margin-bottom: 8px;
      }

      .empty-desc {
        font-size: 14px;
        margin-bottom: 24px;
      }
    }
  }

  .card-collapse {
    border: none;

    ::v-deep .el-collapse-item__header {
      height: auto;
      line-height: 1.5;
      padding: 12px 16px;
      background: #f5f7fa;
      border-radius: 4px;
      margin-bottom: 12px;
      border: 1px solid #ebeef5;
      font-weight: normal;
    }

    ::v-deep .el-collapse-item__wrap {
      border: none;
      background: transparent;
    }

    ::v-deep .el-collapse-item__content {
      padding-bottom: 20px;
    }

    .collapse-title-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding-right: 8px;

      .title-left {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;

        .iccid-label {
          color: #606266;
          font-size: 14px;
        }

        .iccid-value {
          color: #303133;
          font-size: 16px;
          font-weight: 600;
          font-family: 'Courier New', monospace;
        }

        .target-tag {
          margin-left: 8px;
        }
      }

      .title-right {
        display: flex;
        align-items: center;
        gap: 12px;

        .error-tip {
          color: #f56c6c;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 4px;

          i {
            font-size: 16px;
          }
        }
      }
    }
  }

  .section-block {
    margin-bottom: 20px;

    .block-label {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 12px;
      padding-left: 8px;
      border-left: 4px solid #409eff;
    }

    .table-summary {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: 8px;
      padding: 0 8px;
      color: #606266;
      font-size: 13px;

      strong {
        color: #409eff;
      }
    }
  }

  .transfer-form {
    padding: 16px;
    background: #fafafa;
    border-radius: 4px;
    border: 1px solid #ebeef5;

    .agent-text {
      color: #303133;
      font-size: 14px;
    }

    ::v-deep .el-textarea__inner {
      resize: none;
    }
  }

  .add-card-bar {
    margin-top: 16px;
  }

  .page-footer {
    position: fixed;
    right: 0;
    bottom: 0;
    width: calc(100% - 200px);
    height: 60px;
    background: #fff;
    border-top: 1px solid #ebeef5;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 24px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
    z-index: 100;

    .footer-summary {
      display: flex;
      gap: 24px;
      color: #606266;
      font-size: 14px;

      strong {
        color: #303133;
        font-size: 16px;
      }
    }
  }
}

.add-stat {
  text-align: right;
  color: #606266;
  font-size: 13px;
  margin-top: 8px;

  strong {
    color: #409eff;
  }
}

@media (max-width: 768px) {
  .multi-net-turn-page {
    padding: 8px;
    padding-bottom: 80px;

    .page-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }

    .collapse-title-row {
      flex-direction: column;
      align-items: flex-start !important;
      gap: 8px;

      .title-right {
        width: 100%;
        justify-content: flex-end;
      }
    }

    .page-footer {
      width: 100%;
      flex-direction: column;
      height: auto;
      padding: 12px;
      gap: 10px;
    }
  }
}
</style>
